import { resolvePlayerMatchResult } from '../../domain/matchOutcome.js';
import { formatMatchTelemetryStats } from '../../application/formatMatchTelemetryStats.js';

/**
 * createMatchEndBannerController — Post-game overlay + scoreboard (P3-15 / SPY-42).
 */

function escapeText(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function rowHtml(row) {
  return (
    `<tr data-player-id="${escapeText(row.id)}">` +
    `<td>${escapeText(row.team)}</td>` +
    `<td>${escapeText(row.champion)}</td>` +
    `<td>${escapeText(row.level)}</td>` +
    `<td>${escapeText(row.kda)}</td>` +
    `<td>${escapeText(row.cs)}</td>` +
    `<td>${escapeText(row.gold)}</td>` +
    `</tr>`
  );
}

function telemetryHtml(record) {
  const stats = formatMatchTelemetryStats(record);
  return (
    `<div class="match-end-telemetry-row">` +
    `<span>Duración <strong>${escapeText(stats.matchLengthText)}</strong></span>` +
    `<span>FF <strong>${escapeText(stats.forfeitText)}</strong></span>` +
    `<span>Ganador <strong>${escapeText(stats.winTeamText)}</strong></span>` +
    `</div>`
  );
}

function applyDialogA11y(rootEl, visible) {
  if (!rootEl) return;
  rootEl.setAttribute('role', 'dialog');
  rootEl.setAttribute('aria-modal', 'true');
  rootEl.setAttribute('aria-hidden', visible ? 'false' : 'true');
  if (visible) {
    rootEl.setAttribute('aria-labelledby', 'match-end-title');
  }
}

export function createMatchEndBannerController({
  rootEl,
  titleEl,
  summaryTbodyEl,
  telemetryEl,
  continueBtnEl,
  getPlayerTeam,
  getRows,
  getTelemetry
} = {}) {
  let visible = false;
  let summaryRows = [];
  let lastTelemetry = null;

  function renderSummary() {
    const rows = typeof getRows === 'function' ? getRows() : [];
    summaryRows = Array.isArray(rows) ? rows : [];
    if (summaryTbodyEl) {
      summaryTbodyEl.innerHTML = summaryRows.map(rowHtml).join('');
    }
    return summaryRows;
  }

  function renderTelemetry() {
    const record = typeof getTelemetry === 'function' ? getTelemetry() : null;
    lastTelemetry = record || null;
    if (telemetryEl) {
      telemetryEl.innerHTML = record ? telemetryHtml(record) : '';
    }
    return lastTelemetry;
  }

  function show(outcome) {
    if (!rootEl || !titleEl || visible) return false;
    const playerTeam = typeof getPlayerTeam === 'function' ? getPlayerTeam() : null;
    const result = resolvePlayerMatchResult(outcome, playerTeam);
    if (result.kind === 'ongoing') return false;
    titleEl.textContent = result.title;
    rootEl.classList.remove('match-end--victory', 'match-end--defeat');
    rootEl.classList.add(
      result.kind === 'victory' ? 'match-end--victory' : 'match-end--defeat'
    );
    renderSummary();
    renderTelemetry();
    rootEl.hidden = false;
    applyDialogA11y(rootEl, true);
    visible = true;
    if (continueBtnEl && typeof continueBtnEl.focus === 'function') {
      continueBtnEl.focus();
    }
    return true;
  }

  function hide() {
    if (!rootEl) return;
    rootEl.hidden = true;
    applyDialogA11y(rootEl, false);
    if (titleEl) titleEl.textContent = '';
    if (summaryTbodyEl) summaryTbodyEl.innerHTML = '';
    if (telemetryEl) telemetryEl.innerHTML = '';
    summaryRows = [];
    lastTelemetry = null;
    rootEl.classList.remove('match-end--victory', 'match-end--defeat');
    visible = false;
  }

  function isVisible() {
    return visible;
  }

  function getSummaryRows() {
    return summaryRows.slice();
  }

  function getTelemetrySnapshot() {
    return lastTelemetry;
  }

  return {
    show,
    hide,
    isVisible,
    getSummaryRows,
    getTelemetrySnapshot,
    renderSummary,
    renderTelemetry
  };
}
